import { createContext, useContext, useState, useCallback } from "react";

export const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

export const THEME_DEFAULTS = {
    accent: "#EEF2F6",
    accentHover: "#238578",
    accentLight: "rgba(42, 157, 143, 0.10)",

    pageBg: "#101D25",
    cardBg: "#1D282F",
    cardBgEnd: "#212932",
    cardBorder: "#30363D",
    cardShadow: "rgba(42, 157, 143, 0.2)",
    inputBgDark: "#2B323A",
    inputBorder: "#3F454C",

    textPrimary: "#FFFFFF",
    textSecondary: "#647D85",
    textMuted: "#5A6E82",

    ctaBg: "#2A9D8F",
    ctaBgEnd: "",
    ctaText: "#FFFFFF",
    ctaHover: "#238578",

    focusRing: "rgba(42, 157, 143, 0.25)",
    activeBorder: "#2A9D8F",

    shellBg: "#1B2838",

    headerBg: "#FFFFFF",
    headerText: "#000000",

    dangerColor: "#FF6467",
    successColor: "#22C55E",
    tableColor: "#3D424D",

    dashboardBg: "#F5F7F9",

    primary: "#36A09E",
    secondary: "#253B45",
    tertiary: "#4AB9B7",
    textColor: "#777C83",
    dashboardTextColor: "#141820",
    textBold: "#000000",
    textGray: "#757B82",
    // textGray: "#595E65",
    inputBg: "#f0f0f0",

    font: '"Montserrat", sans-serif',
};

export const ThemeProvider = ({ children }) => {
    const [themeData, setThemeData] = useState(null);

    const applyTheme = useCallback((theme) => {
        const activeTheme = theme?.is_enabled ? theme : null;
        setThemeData(activeTheme);

        const root = document.documentElement;
        const D = THEME_DEFAULTS;
        theme = activeTheme;

        // --- Accent / Action ---
        root.style.setProperty("--accent", theme?.accent_color || D.accent);
        root.style.setProperty(
            "--accent-hover",
            theme?.accent_hover_color || D.accentHover,
        );
        root.style.setProperty(
            "--accent-light",
            theme?.accent_light_color || D.accentLight,
        );

        // --- Surfaces ---
        root.style.setProperty("--page-bg", theme?.page_bg_color || D.pageBg);
        const cardBg = theme?.card_bg_color || D.cardBg;
        root.style.setProperty("--card-bg", cardBg);
        root.style.setProperty(
            "--card-bg-end",
            theme?.card_bg_end_color || cardBg,
        );
        root.style.setProperty(
            "--card-border",
            theme?.card_border_color || D.cardBorder,
        );
        root.style.setProperty(
            "--card-shadow",
            theme?.card_shadow_color || D.cardShadow,
        );
        root.style.setProperty(
            "--input-bg-dark",
            theme?.input_bg_dark_color || D.inputBgDark,
        );
        root.style.setProperty(
            "--input-border",
            theme?.input_border_color || D.inputBorder,
        );

        // --- Text ---
        root.style.setProperty(
            "--text-primary",
            theme?.text_primary_color || D.textPrimary,
        );
        root.style.setProperty(
            "--text-secondary",
            theme?.text_secondary_color || D.textSecondary,
        );
        root.style.setProperty(
            "--text-muted",
            theme?.text_muted_color || D.textMuted,
        );

        // --- CTA ---
        const ctaBg = theme?.cta_bg_color || D.ctaBg;
        root.style.setProperty("--cta-bg", ctaBg);
        root.style.setProperty(
            "--cta-bg-end",
            theme?.cta_bg_end_color || ctaBg,
        );
        root.style.setProperty(
            "--cta-text",
            theme?.cta_text_color || D.ctaText,
        );
        root.style.setProperty(
            "--cta-hover",
            theme?.cta_hover_color || D.ctaHover,
        );

        // --- Focus ---
        root.style.setProperty(
            "--focus-ring",
            theme?.focus_ring_color || D.focusRing,
        );
        root.style.setProperty(
            "--active-border",
            theme?.active_border_color || D.activeBorder,
        );

        // --- Shell ---
        root.style.setProperty(
            "--shell-bg",
            theme?.shell_bg_color || D.shellBg,
        );

        // --- Header ---
        root.style.setProperty(
            "--header-bg",
            theme?.header_bg_color || D.headerBg,
        );
        root.style.setProperty(
            "--header-text",
            theme?.header_text_color || D.headerText,
        );

        // --- Danger ---
        root.style.setProperty(
            "--danger",
            theme?.danger_color || D.dangerColor,
        );
        root.style.setProperty(
            "--success",
            theme?.success_color || D.successColor,
        );
        root.style.setProperty(
            "--table-color",
            theme?.table_color || D.tableColor,
        );

        // --- Dashboard ---
        root.style.setProperty(
            "--dashboard-bg",
            theme?.dashboard_bg_color || D.dashboardBg,
        );

        // --- App tokens (backward-compatible) ---
        const primary = theme?.primary_color || D.primary;
        root.style.setProperty("--primary", primary);
        root.style.setProperty("--primary-20", `${primary}33`);
        root.style.setProperty("--primary-30", `${primary}4D`);
        root.style.setProperty("--primary-40", `${primary}66`);
        root.style.setProperty("--primary-50", `${primary}80`);
        root.style.setProperty("--primary-75", `${primary}BF`);

        const secondary = theme?.secondary_color || D.secondary;
        root.style.setProperty("--secondary", secondary);
        root.style.setProperty("--secondary-20", `${secondary}33`);
        root.style.setProperty("--secondary-30", `${secondary}4D`);
        root.style.setProperty("--secondary-40", `${secondary}66`);
        root.style.setProperty("--secondary-50", `${secondary}80`);
        root.style.setProperty("--secondary-75", `${secondary}BF`);

        const tertiary = theme?.tertiary_color || D.tertiary;
        root.style.setProperty("--tertiary", tertiary);
        root.style.setProperty("--tertiary-50", `${tertiary}80`);

        root.style.setProperty(
            "--text-color",
            theme?.text_color || D.textColor,
        );
        root.style.setProperty(
            "--dashboard-text-color",
            theme?.dashboard_text_color || D.dashboardTextColor,
        );
        root.style.setProperty(
            "--text-bold-color",
            theme?.bold_text_color || D.textBold,
        );
        root.style.setProperty(
            "--text-gray-color",
            theme?.gray_color || D.textGray,
        );
        root.style.setProperty(
            "--gray-05",
            theme?.gray_color ? `${theme?.gray_color}0D` : `${D.textGray}0D`,
        );
        root.style.setProperty(
            "--gray-20",
            theme?.gray_color ? `${theme?.gray_color}33` : `${D.textGray}33`,
        );

        root.style.setProperty(
            "--success-bg",
            theme?.success_color
                ? `${theme?.success_color}1a`
                : `${D.successColor}1a`,
        );

        root.style.setProperty(
            "--danger-bg",
            theme?.danger_color
                ? `${theme?.danger_color}1a`
                : `${D.dangerColor}1a`,
        );

        root.style.setProperty(
            "--bg-input-color",
            theme?.bg_input_color || D.inputBg,
        );

        // Font
        if (theme?.font) {
            root.style.setProperty(
                "--font-family",
                `'${theme.font}', sans-serif`,
            );
        } else {
            root.style.setProperty("--font-family", D.font);
        }

        // Favicon
        if (theme?.logo_url) {
            const links = document.querySelectorAll("link[rel*='icon']");
            if (links.length > 0) {
                links.forEach((link) => {
                    link.href = theme.logo_url;
                });
            } else {
                const newLink = document.createElement("link");
                newLink.rel = "icon";
                newLink.href = theme.logo_url;
                document.head.appendChild(newLink);
            }
        }
    }, []);

    return (
        <ThemeContext.Provider value={{ themeData, applyTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
